import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Heading,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from "@chakra-ui/react";
import DataTable from "./AppDataTable";
import apiService from "../services/ApiService";
import { tableCustomStyles } from "../themes/TableStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartBar,
  faSyringe,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import * as IconButton from "../themes/IconButtonSet";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";

const VaccineInventoryTable = ({ warehouseId, sessionId }) => {
  const [inventoryData, setInventoryData] = useState([]);
  const [lotDetails, setLotDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVaccine, setSelectedVaccine] = useState(null);

  // เรียก API เพื่อดึงข้อมูลวัคซีนคงเหลือ
  useEffect(() => {
    if (warehouseId) {
      const fetchInventory = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const response = await apiService({
            method: "POST",
            url: "/getPublicVaccineInventoryByLot",
            data: { WarehouseID: warehouseId },
            sessionId: sessionId || "",
          });
          if (response.result === "success") {
            // รวมยอด TotalQuantity สำหรับแต่ละ VaccineID
            const groupedData = response.InventoryReport.reduce(
              (acc, current) => {
                const {
                  VaccineID,
                  VaccineNameTH,
                  VaccineNameEN,
                  PriceDistribution,
                  TotalQuantity,
                  LotID,
                  ProductionDate,
                  ExpiryDate,
                } = current;
                if (!acc[VaccineID]) {
                  acc[VaccineID] = {
                    VaccineID,
                    VaccineNameTH,
                    VaccineNameEN,
                    PriceDistribution,
                    TotalQuantity: 0,
                    Lots: [],
                  };
                }
                acc[VaccineID].TotalQuantity += TotalQuantity;
                acc[VaccineID].Lots.push({
                  LotID,
                  ProductionDate,
                  ExpiryDate,
                  TotalQuantity,
                });
                return acc;
              },
              {}
            );
            setInventoryData(Object.values(groupedData));
          } else {
            setError("ไม่สามารถดึงข้อมูลวัคซีนคงเหลือได้");
          }
        } catch (err) {
          setError("เกิดข้อผิดพลาดในการเรียก API");
        } finally {
          setIsLoading(false);
        }
      };
      fetchInventory();
    }
  }, [warehouseId, sessionId]);

  // ฟังก์ชันสำหรับเปิด Modal และแสดงข้อมูล Lot
  const handleShowLotDetails = (row) => {
    setSelectedVaccine(row);
    setLotDetails(row.Lots);
    setIsModalOpen(true);
  };

  // กำหนดคอลัมน์สำหรับตารางหลัก
  const columns = [
    {
      name: "ชื่อวัคซีน (TH)",
      selector: (row) => row.VaccineNameTH,
      sortable: true,
    },
    {
      name: "ราคาจำหน่าย/ขวด (บาท)",
      selector: (row) => row.PriceDistribution,
      sortable: true,
      width: "200px",
    },
    {
      name: "จำนวนคงเหลือ (ขวด)",
      selector: (row) => row.TotalQuantity,
      sortable: true,
      format: (row) => row.TotalQuantity.toLocaleString("th-TH"),
      width: "200px",
    },
    {
      name: "รายละเอียด",
      button: true,
      cell: (row) => (
        <IconButton.View
          icon={faInfoCircle}
          onClick={() => handleShowLotDetails(row)}
          width="100px"
          mr={5}
        />
      ),
    },
  ];

  // กำหนดคอลัมน์สำหรับตารางใน Modal
  const lotColumns = [
    /*
    {
      name: "Lot No.",
      selector: (row) => row.LotID,
      sortable: true,
    },
    */
    {
      name: "วันผลิต",
      selector: (row) => row.ProductionDate,
      sortable: true,
      format: (row) =>
        new Date(row.ProductionDate).toLocaleDateString("th-TH"),
    },
    {
      name: "วันหมดอายุ",
      selector: (row) => row.ExpiryDate,
      sortable: true,
      format: (row) => new Date(row.ExpiryDate).toLocaleDateString("th-TH"),
    },
    {
      name: "จำนวนคงเหลือ (ขวด)",
      selector: (row) => row.TotalQuantity,
      sortable: true,
      format: (row) => row.TotalQuantity.toLocaleString("th-TH"),
    },
  ];

  return (
    <Box mt={5} p={5}>
        <StandardHeader
          title="สรุปยอดวัคซีนคงเหลือ"
          bgColor="iBlue.600"
          icon={faSyringe}
        />
      {isLoading ? (
        <Text textAlign="center" color="gray.500" p={5}>
          กำลังโหลดข้อมูลวัคซีนคงเหลือ...
        </Text>
      ) : error ? (
        <Text textAlign="center" color="red.500" p={5}>
          {error}
        </Text>
      ) : inventoryData.length > 0 ? (
        <DataTable
          columns={columns}
          data={inventoryData}
          highlightOnHover
          striped
          responsive
          customStyles={tableCustomStyles}
        />
      ) : (
        <Text textAlign="center" color="gray.500" p={5}>
          ไม่มีข้อมูลวัคซีนคงเหลือ
        </Text>
      )}

      {/* Modal สำหรับแสดงรายละเอียด Lot */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        size="2xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            รายละเอียด Lot - {selectedVaccine?.VaccineNameTH}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box mb={10}>
              <DataTable
                columns={lotColumns}
                data={lotDetails}
                highlightOnHover
                striped
                responsive
                customStyles={tableCustomStyles}
              />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default VaccineInventoryTable;
